import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { isNativeAppWebView, useIsNativeApp } from '@hooks/useIsNativeApp'
import InstallAppBanner from './InstallAppBanner'

const INSTALL_APP_SHOWN_KEY = 'installAppShownBanner'

const InstallAppModal = () => {
  const [showBanner, setShowBanner] = useState(false)
  const eventRef = useRef<any>(null)
  const [isIOS, setIsIOS] = useState(false)
  const [isAndroid, setIsAndroid] = useState(false)
  const router = useRouter()
  const isNativeApp = useIsNativeApp()

  useEffect(() => {
    // Check `window` directly: `isNativeApp` is still false on the first run, and a
    // banner timer scheduled then would open the banner inside the native app.
    if (isNativeAppWebView()) return
    // Every scheduled banner timer, so cleanup can cancel all of them
    const bannerTimers: number[] = []
    // Check for iOS / Android
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent)
    const isAndroidDevice = /android/.test(userAgent)
    setIsIOS(isIosDevice)
    setIsAndroid(isAndroidDevice)

    const handleBeforeInstallPrompt = async (e: any) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later.
      eventRef.current = e
      console.log(`'beforeinstallprompt' event was fired.`)

      if (localStorage.getItem(INSTALL_APP_SHOWN_KEY) !== 'true') {
        bannerTimers.push(
          window.setTimeout(() => {
            setShowBanner(true)
          }, 1000 * 5),
        )
      }
    }

    const handleAppInstalled = () => {
      window?.umami?.track('app-installed')
      localStorage.setItem(INSTALL_APP_SHOWN_KEY, 'true')
      setShowBanner(false)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // iOS Logic: Show banner if on iOS and not standalone (installed)
    // Standalone check for iOS Safari
    const isStandalone = (window.navigator as any).standalone === true
    if (isIosDevice && !isStandalone) {
      // Check if already shown
      if (localStorage.getItem(INSTALL_APP_SHOWN_KEY) !== 'true') {
        bannerTimers.push(
          window.setTimeout(() => {
            setShowBanner(true)
          }, 1000 * 5),
        )
      }
    }

    // Android Logic: Fallback manual banner if native event doesn't fire
    // Primary method is native beforeinstallprompt, this is backup for dev/testing
    if (isAndroidDevice) {
      if (localStorage.getItem(INSTALL_APP_SHOWN_KEY) !== 'true') {
        bannerTimers.push(
          window.setTimeout(() => {
            // Only show if native event hasn't fired yet
            if (!eventRef.current) {
              setShowBanner(true)
            }
          }, 1000 * 10), // 10 seconds - longer to give native event a chance
        )
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      bannerTimers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [])

  // Update CSS variable for layout coordination
  useEffect(() => {
    if (showBanner) {
      document.documentElement.style.setProperty('--banner-height', '64px')
    } else {
      document.documentElement.style.setProperty('--banner-height', '0px')
    }

    // Cleanup on unmount
    return () => {
      document.documentElement.style.setProperty('--banner-height', '0px')
    }
  }, [showBanner])

  const handleInstallClick = async () => {
    // Android: use native prompt if available, otherwise open Play Store
    if (isAndroid) {
      // If we have the native prompt, use it
      if (eventRef.current) {
        eventRef.current.prompt()
        const { outcome } = await eventRef.current.userChoice
        console.log(`User response to the install prompt: ${outcome}`)
        eventRef.current = null
        setShowBanner(false)
        return
      }

      // Fallback: direct Play Store link
      const packageId = 'com.hayirhah.app'
      const marketUrl = `market://details?id=${packageId}`
      const playStoreUrl = `https://play.google.com/store/apps/details?id=${packageId}&hl=en`
      window.location.href = marketUrl
      setTimeout(() => {
        window.location.href = playStoreUrl
      }, 800)
      setShowBanner(false)
      return
    }

    // If we have the native prompt (Desktop), use it
    if (eventRef.current) {
      eventRef.current.prompt()
      const { outcome } = await eventRef.current.userChoice
      console.log(`User response to the install prompt: ${outcome}`)
      eventRef.current = null
      setShowBanner(false)
      return
    }

    // If we are on iOS (or forced fallback), redirect to instruction page
    if (isIOS) {
      router.push('/uygulama')
      setShowBanner(false)
    }
  }

  const handleClose = () => {
    localStorage.setItem(INSTALL_APP_SHOWN_KEY, 'true')
    setShowBanner(false)
  }

  // Never offer an app install inside the app itself
  if (!showBanner || isNativeApp) return null

  return (
    <InstallAppBanner
      onInstall={handleInstallClick}
      onClose={handleClose}
      ctaLabel={isAndroid ? 'Play Store’da Aç' : 'Yükle'}
      subtitle={isAndroid ? 'Google Play' : 'Ücretsiz - Uygulama'}
    />
  )
}

export default InstallAppModal
